import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  RestoreFromTrash as RestoreIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FieldMappingManager = ({ open, onClose, campaign = null }) => {
  const { token } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [defaultMappings, setDefaultMappings] = useState([]);
  const [campaignExamples, setCampaignExamples] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newCampaign, setNewCampaign] = useState(campaign || '');
  const [editingMapping, setEditingMapping] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      fetchMappings();
      fetchDefaults();
    }
  }, [open, campaign]);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (campaign) params.append('campaign', campaign);
      params.append('field_type', 'lead');

      const response = await axios.get(`${API_BASE_URL}/field-mappings?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMappings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
      showSnackbar('Error fetching field mappings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaults = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/field-mappings/defaults`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDefaultMappings(response.data.data.defaults);
        setCampaignExamples(response.data.data.examples);
      }
    } catch (error) {
      console.error('Error fetching defaults:', error);
    }
  };

  const saveMappings = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/field-mappings`,
        { mappings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar('Field mappings saved successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving mappings:', error);
      showSnackbar('Error saving field mappings', 'error');
    }
  };

  const createCampaignFromTemplate = async () => {
    if (!selectedTemplate || !newCampaign) {
      showSnackbar('Please select a template and enter campaign name', 'warning');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/field-mappings/campaign`,
        {
          campaign: newCampaign,
          template: selectedTemplate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar(`Created ${newCampaign} campaign mappings`, 'success');
        fetchMappings();
        setSelectedTemplate('');
        setNewCampaign('');
      }
    } catch (error) {
      console.error('Error creating campaign mappings:', error);
      showSnackbar('Error creating campaign mappings', 'error');
    }
  };

  const initializeDefaults = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/field-mappings/initialize`,
        { campaign },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar('Default mappings initialized', 'success');
        fetchMappings();
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      showSnackbar('Error initializing default mappings', 'error');
    }
  };

  const updateMapping = (index, field, value) => {
    const updatedMappings = [...mappings];
    updatedMappings[index] = {
      ...updatedMappings[index],
      [field]: value
    };
    setMappings(updatedMappings);
  };

  const deleteMapping = (index) => {
    const updatedMappings = mappings.filter((_, i) => i !== index);
    setMappings(updatedMappings);
  };

  const addNewMapping = () => {
    const newMapping = {
      campaign: campaign,
      field_type: 'lead',
      original_field: '',
      custom_label: '',
      description: '',
      is_required: false,
      is_visible: true,
      display_order: mappings.length + 1
    };
    setMappings([...mappings, newMapping]);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getAvailableFields = () => {
    const usedFields = new Set(mappings.map(m => m.original_field));
    return defaultMappings.filter(dm => !usedFields.has(dm.original_field));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {campaign ? `Field Mappings - ${campaign}` : 'Global Field Mappings'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={saveMappings}
            disabled={loading}
          >
            Save Changes
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Campaign Templates */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Campaign Templates</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Quick Setup</AlertTitle>
                  Use pre-configured templates for common campaign types to quickly set up custom field labels.
                </Alert>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Template</InputLabel>
                      <Select
                        value={selectedTemplate}
                        label="Template"
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                      >
                        <MenuItem value="">Select Template</MenuItem>
                        {Object.keys(campaignExamples).map(template => (
                          <MenuItem key={template} value={template}>
                            {template}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Campaign Name"
                      value={newCampaign}
                      onChange={(e) => setNewCampaign(e.target.value)}
                      placeholder="e.g., Short Term Miway"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      variant="contained"
                      onClick={createCampaignFromTemplate}
                      disabled={!selectedTemplate || !newCampaign}
                      fullWidth
                    >
                      Create from Template
                    </Button>
                  </Grid>
                </Grid>

                {/* Template Preview */}
                {selectedTemplate && campaignExamples[selectedTemplate] && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Template Preview: {selectedTemplate}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {campaignExamples[selectedTemplate].map((mapping, index) => (
                        <Chip
                          key={index}
                          label={`${mapping.original_field} → ${mapping.custom_label}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Current Mappings */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Current Field Mappings</Typography>
              <Box>
                <Button
                  startIcon={<RestoreIcon />}
                  onClick={initializeDefaults}
                  sx={{ mr: 1 }}
                >
                  Initialize Defaults
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={addNewMapping}
                >
                  Add Field
                </Button>
              </Box>
            </Box>

            {mappings.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography color="textSecondary" align="center">
                    No field mappings configured. Click "Initialize Defaults" to start with standard field labels.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {mappings.map((mapping, index) => (
                  <Grid item xs={12} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={3}>
                            <FormControl fullWidth>
                              <InputLabel>Original Field</InputLabel>
                              <Select
                                value={mapping.original_field}
                                label="Original Field"
                                onChange={(e) => updateMapping(index, 'original_field', e.target.value)}
                              >
                                {getAvailableFields().map(field => (
                                  <MenuItem key={field.original_field} value={field.original_field}>
                                    {field.original_field}
                                  </MenuItem>
                                ))}
                                {mapping.original_field && (
                                  <MenuItem value={mapping.original_field}>
                                    {mapping.original_field}
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              fullWidth
                              label="Custom Label"
                              value={mapping.custom_label}
                              onChange={(e) => updateMapping(index, 'custom_label', e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              label="Description"
                              value={mapping.description || ''}
                              onChange={(e) => updateMapping(index, 'description', e.target.value)}
                              placeholder="Optional description or help text"
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={mapping.is_visible}
                                  onChange={(e) => updateMapping(index, 'is_visible', e.target.checked)}
                                />
                              }
                              label="Visible"
                              labelPlacement="top"
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              color="error"
                              onClick={() => deleteMapping(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={saveMappings}
          disabled={loading}
          startIcon={<SaveIcon />}
        >
          Save All Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldMappingManager; 